import { ArrowRight } from "lucide-react";
import { LocationPicker } from "@/components/LocationPicker";
import { earliestLocal, toLocalInput } from "@/components/booking/TripOptions";
import { when } from "@/lib/rides";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const EMPTY_LEGS = [{ pickup: null, dropoff: null, at: "" }, { pickup: null, dropoff: null, at: "" }];

export function buildSplit(legs, opts, repeat) {
  if (legs.some((l) => !l.pickup || !l.dropoff || !l.at)) return null;
  if (repeat && !opts.days.length) return null;
  return {
    legs: legs.map((l) => ({ pickup: l.pickup, dropoff: l.dropoff, scheduled_at: new Date(l.at).toISOString(), passengers: opts.passengers || 1 })),
    vehicle_class: opts.vehicle, recurrence: repeat ? { days: opts.days, weeks: opts.weeks } : null, recurring_payment: opts.payment,
    discount_code: opts.discountCode || null, payment_method: opts.payMethod,
    secondary_payment_method: opts.payMethod === "ride_credit" ? opts.payRemainder : null,
  };
}

function Leg({ n, leg, set, lead }) {
  return (
    <div className="relative space-y-2 rounded-2xl border border-white/10 bg-card p-4 pl-9" data-testid={`split-leg-${n}`}>
      <span className="absolute left-3 top-4 grid h-5 w-5 place-items-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">{n}</span>
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Leg {n} · required</p>
      <LocationPicker kind={`leg${n}-pickup`} label={`Leg ${n} pickup`} value={leg.pickup} onChange={(p) => set({ pickup: p })} allowGps placeholder="Pickup" />
      <LocationPicker kind={`leg${n}-dropoff`} label={`Leg ${n} destination`} value={leg.dropoff} onChange={(p) => set({ dropoff: p })} allowGps placeholder="Destination" />
      <Input type="datetime-local" min={earliestLocal(lead)} value={leg.at} onChange={(e) => set({ at: e.target.value })}
        className="h-10 rounded-full [color-scheme:dark]" data-testid={`split-leg-${n}-time`} />
    </div>
  );
}

export function SplitLegs({ legs, setLegs, lead }) {
  const setLeg = (i) => (patch) => setLegs((ls) => {
    const next = ls.map((l, j) => (j === i ? { ...l, ...patch } : l));
    if (i === 0 && patch.dropoff && !next[1].pickup) next[1] = { ...next[1], pickup: patch.dropoff };
    if (i === 0 && patch.pickup && !next[1].dropoff) next[1] = { ...next[1], dropoff: patch.pickup };
    return next;
  });
  return (
    <div className="space-y-2" data-testid="split-legs">
      <p className="text-xs text-muted-foreground">Two linked rides under one booking. Each leg has its own pickup, destination and time, and its own 6-hour change cutoff.</p>
      <Leg n={1} leg={legs[0]} set={setLeg(0)} lead={lead} />
      <div className="flex justify-center"><ArrowRight className="h-4 w-4 rotate-90 text-muted-foreground" /></div>
      <Leg n={2} leg={legs[1]} set={setLeg(1)} lead={lead} />
    </div>
  );
}

export function SplitNotice({ schedule, setLegs }) {
  if (!schedule || schedule.ok) return null;
  return (
    <div className="space-y-2 rounded-2xl border border-destructive/40 bg-destructive/10 p-3 text-sm" data-testid="split-schedule-notice">
      {schedule.problems.map((p, i) => (
        <div key={i} className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs"><span className="font-semibold">Leg {p.leg}:</span> {p.message}. Next available {when(p.next_available)}.</p>
          <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" data-testid={`split-use-next-${p.leg}`}
            onClick={() => setLegs((ls) => ls.map((l, j) => (j === p.leg - 1 ? { ...l, at: toLocalInput(p.next_available) } : l)))}>Use this time</Button>
        </div>
      ))}
    </div>
  );
}
